import { EditableText } from "../../EditableText";
import type { PhotoGalleryGroup } from "../../details-section/Photos/photos.types";
import { PhotoReportPhotoCard } from "./PhotoReportPhotoCard";
import type { PhotoSelectionState } from "./photoReport.utils";
import { getPhotoRef, isPhotoIncluded } from "./photoReport.utils";

interface PhotoReportRoomSectionProps {
  group: PhotoGalleryGroup;
  /** Current (editable) room heading shown for this group. */
  titleValue: string;
  onTitleChange: (title: string) => void;
  selection: PhotoSelectionState;
  /** galleryId → live global-sequential number, only for included photos. */
  photoNumberById: Map<string, number>;
  showPhotoNumberOverlay: boolean;
  includeCaptions: boolean;
  onTogglePhoto: (galleryId: string) => void;
  onCaptionChange: (galleryId: string, caption: string) => void;
  onToggleRoom: (galleryIds: string[], include: boolean) => void;
}

export const PhotoReportRoomSection = ({
  group,
  titleValue,
  onTitleChange,
  selection,
  photoNumberById,
  showPhotoNumberOverlay,
  includeCaptions,
  onTogglePhoto,
  onCaptionChange,
  onToggleRoom,
}: PhotoReportRoomSectionProps) => {
  const exportablePhotos = group.photos.filter((photo) => getPhotoRef(photo) !== null);
  if (exportablePhotos.length === 0) {
    return null;
  }

  const galleryIds = exportablePhotos.map((photo) => photo.galleryId);
  const includedCount = galleryIds.filter((id) => isPhotoIncluded(selection, id)).length;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between border-b pb-1.5">
        <h4 className="flex items-baseline text-sm font-semibold text-gray-800">
          <EditableText value={titleValue} onChange={onTitleChange} placeholder="Room name" />
          <span className="ml-2 text-xs font-normal text-gray-500">
            {includedCount}/{exportablePhotos.length}
          </span>
        </h4>
        <button
          type="button"
          className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
          onClick={() => onToggleRoom(galleryIds, includedCount === 0)}
        >
          {includedCount === 0 ? "Include room" : "Exclude room"}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {exportablePhotos.map((item) => (
          <PhotoReportPhotoCard
            key={item.galleryId}
            item={item}
            roomTitle={titleValue}
            photoNumber={photoNumberById.get(item.galleryId)}
            included={isPhotoIncluded(selection, item.galleryId)}
            caption={selection.get(item.galleryId)?.caption ?? ""}
            showPhotoNumberOverlay={showPhotoNumberOverlay}
            includeCaptions={includeCaptions}
            onToggleIncluded={() => onTogglePhoto(item.galleryId)}
            onCaptionChange={(caption) => onCaptionChange(item.galleryId, caption)}
          />
        ))}
      </div>
    </section>
  );
};
